import Box from '@mui/material/Box';
import { t } from 'i18next';
import { memo, useEffect } from 'react';
import { Control, UseFormSetValue, useWatch } from 'react-hook-form';

import AutocompleteAddOnsTypeahead from '~components/FormFields/CommonAutocompletes/AutocompleteAddOnsTypeahead';
import { useCompanyCurrency } from '~hooks/useCompanyCurrency/useCompanyCurrency';
import useAddOnQuery from '~queries/useAddOnQuery';

import { EM_DASH } from '../SettlementDetails/constants';
import {
  formatAccountingAmount,
  getAddOnRateTypeLabel,
  SettlementSheetAmountInput,
  SettlementSheetCell,
  SettlementSheetRemoveButton,
  settlementSheetRowSx,
} from './settlementSheetDisplay';
import {
  EMPTY_NEW_ADD_ON_CHARGE,
  isPercentageDraft,
  SettlementSheetFormValues,
} from './settlementSheetForm';
import { isPercentageRate } from './settlementSheetLines';

interface NewSettlementAddOnChargeRowProps {
  companyId: string;
  control: Control<SettlementSheetFormValues>;
  index: number;
  onRemove: (index: number) => void;
  setValue: UseFormSetValue<SettlementSheetFormValues>;
  totalAmount: number | null;
}

function NewSettlementAddOnChargeRow({
  companyId,
  control,
  index,
  onRemove,
  setValue,
  totalAmount,
}: NewSettlementAddOnChargeRowProps) {
  const { currencyFormatter } = useCompanyCurrency();
  const draft =
    useWatch({ control, name: `newAddOnCharges.${index}` }) ?? EMPTY_NEW_ADD_ON_CHARGE;
  const { addOn } = useAddOnQuery(companyId, draft.addOn?.value ?? null);
  const isPercentage = isPercentageDraft(draft);

  useEffect(() => {
    if (!addOn?.id || addOn.id === draft.prefilledAddOnId) {
      return;
    }

    const isPercentageAddOn = isPercentageRate(
      addOn.revenueCostRateType,
      addOn.percentage,
    );

    setValue(`newAddOnCharges.${index}.isPercentage`, isPercentageAddOn);
    setValue(`newAddOnCharges.${index}.quantity`, addOn.quantity);
    setValue(
      `newAddOnCharges.${index}.rate`,
      isPercentageAddOn ? addOn.percentage : (addOn.rate ?? 0),
    );
    setValue(`newAddOnCharges.${index}.prefilledAddOnId`, addOn.id);
  }, [addOn, draft.prefilledAddOnId, index, setValue]);

  return (
    <Box data-test-id="settlement-sheet-new-add-on-charge" sx={settlementSheetRowSx}>
      <AutocompleteAddOnsTypeahead
        companyId={companyId}
        control={control}
        isRequired
        name={`newAddOnCharges.${index}.addOn`}
        sx={{ gridColumn: 'date / rateType' }}
      />
      {draft.addOn && (
        <>
          <SettlementSheetCell column="rateType">
            {getAddOnRateTypeLabel(draft.addOn._entity.addOnRateType)}
          </SettlementSheetCell>
          {!isPercentage && (
            <SettlementSheetAmountInput
              column="billingQuantity"
              control={control}
              label={t('approvals.driver_pay.settlements.pdf.billing_qty')}
              name={`newAddOnCharges.${index}.quantity`}
            />
          )}
          <SettlementSheetAmountInput
            adornment={isPercentage ? 'percentage' : 'currency'}
            column="rate"
            control={control}
            label={t('approvals.driver_pay.tabs.driver_pay.rate')}
            name={`newAddOnCharges.${index}.rate`}
          />
        </>
      )}
      <SettlementSheetCell column="total" isRightAligned>
        {totalAmount === null
          ? EM_DASH
          : formatAccountingAmount(totalAmount, currencyFormatter)}
      </SettlementSheetCell>
      <SettlementSheetRemoveButton
        label={t('actions.remove')}
        onClick={() => onRemove(index)}
      />
    </Box>
  );
}

export default memo(NewSettlementAddOnChargeRow);
